import { useLingui } from '@lingui/react/macro';
import { Copy, Download, Pencil, Trash2 } from 'lucide-react';
import { MediaManagerTemplate } from 'modules/core/domain/interfaces/supabase/media-manager/entity';
import { mediaKindLabel } from 'modules/manager/domain/media-kind';
import React from 'react';
import { theme } from 'styles/theme/theme';
import { UDButton, UDText } from 'ud-ui/components';
import EmptyButton from 'ud-ui/components/empty-button';
import UDTippy from 'ud-ui/components/tippy';
import MediaManagerPresetPreview from './media-manager-preset-preview';
import { PRESET_CARD_ACTIONS_GAP, PRESET_CARD_CLASS } from './preset-card-metrics';

interface IProps {
  template: MediaManagerTemplate;
  isAdmin?: boolean;
  canDuplicate?: boolean;
  /** A copy is already being created — the control stays blocked until it lands. */
  isDuplicating?: boolean;
  onOpen: (template: MediaManagerTemplate) => void;
  onDuplicate: (template: MediaManagerTemplate) => void;
  onEdit: (template: MediaManagerTemplate) => void;
  onDelete: (template: MediaManagerTemplate) => void;
  /** Available to everyone: a preset can be duplicated by anyone, so its file is no secret. */
  onExport: (template: MediaManagerTemplate) => void;
  isExporting?: boolean;
}

function MediaManagerPresetCard(props: IProps) {
  const { template, isAdmin, canDuplicate, isDuplicating, isExporting, onOpen, onDuplicate, onEdit, onDelete, onExport } = props;
  const { t } = useLingui();
  const typeLabel = t(mediaKindLabel(template.mediaKind));

  return (
    <div className={PRESET_CARD_CLASS}>
      <MediaManagerPresetPreview template={template} />

      <div className="flex min-w-0 flex-col">
        <UDText variant="body-2" className="truncate font-bold">
          {template.name}
        </UDText>
        <UDText variant="body-3" className="text-gray-400">
          {typeLabel}
        </UDText>
      </div>

      <div className="flex items-center justify-between">
        <UDButton variant="primary" onClick={() => onOpen(template)}>
          {isAdmin ? t`Открыть` : t`Просмотр`}
        </UDButton>
        <div className="flex items-center" style={{ gap: PRESET_CARD_ACTIONS_GAP }}>
          <UDTippy content={t`Скачать файл`} placement={'top'}>
            <EmptyButton
              icon={<Download size={20} />}
              disabled={isExporting}
              aria-label={t`Скачать файл`}
              onClick={() => onExport(template)}
            />
          </UDTippy>
          {canDuplicate && (
            <UDTippy content={t`Дублировать в свой турнир`} placement={'top'}>
              <EmptyButton
                icon={<Copy size={20} />}
                disabled={isDuplicating}
                loading={isDuplicating}
                loaderSize={20}
                onClick={() => onDuplicate(template)}
              />
            </UDTippy>
          )}
          {isAdmin && (
            <>
              <UDTippy content={t`Редактировать`} placement={'top'}>
                <EmptyButton icon={<Pencil size={20} />} onClick={() => onEdit(template)} />
              </UDTippy>
              <UDTippy content={t`Удалить`} placement={'top'}>
                <EmptyButton color={theme.colors.error} icon={<Trash2 size={20} />} onClick={() => onDelete(template)} />
              </UDTippy>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default MediaManagerPresetCard;
